import { arrayUnion, arrayRemove, increment, serverTimestamp } from 'firebase/firestore';

// Marqueur posé par chaque écriture de goût du client actuel (chemin pointé, jamais
// de remplacement de blockPreferences) : ce client ne recopie plus un vote de plat
// sur ses ingrédients, ses refus d'ingrédients sont donc délibérés. Un profil
// marqué n'est jamais « nettoyé » par les triggers onProfileCreatedTastes /
// onProfileTastesChanged ni par une relance de la migration (spec 2026-09-23, §5).
const PROPAGATION_FREE = 'blockPreferences.propagationFree';

/** Vote neutre du quiz : compteur et horodatage seulement, aucune liste touchée. */
export function quizCounterOnlyUpdates() {
  return {
    'blockPreferences.votesCount': increment(1),
    'blockPreferences.lastQuizAt': serverTimestamp(),
    [PROPAGATION_FREE]: true,
    updatedAt: serverTimestamp()
  };
}

/**
 * Construit les updates Firestore à appliquer au profil après un vote.
 *
 * Les scores blocs sont calculés à la volée côté client via Wilson
 * sur l'exposure (computeEffectiveBlockScores). On ne maintient plus
 * de cache `blockPreferences.scores`. Source de vérité = dishLikes /
 * dishDislikes + le catalogue products (qui définit les blocs de chaque plat).
 *
 * @param {object} voteEvent - {
 *   kind: 'dish' | 'block',
 *   itemId: string,
 *   vote: 'like' | 'dislike'
 * }
 * @returns {object} updates à passer à updateDoc()
 */
export function applyVoteToProfile(voteEvent) {
  const { kind, itemId, vote } = voteEvent;
  if (!kind || !itemId || (vote !== 'like' && vote !== 'dislike')) {
    throw new Error('Invalid voteEvent');
  }
  const updates = quizCounterOnlyUpdates();

  if (kind === 'dish') {
    if (vote === 'like') {
      updates['blockPreferences.dishLikes'] = arrayUnion(itemId);
      updates['blockPreferences.dishDislikes'] = arrayRemove(itemId);
      // Un « j'aime » lève aussi la mauvaise note (refus certain d'un avis 1-2 étoiles).
      updates['blockPreferences.lowRatedDishes'] = arrayRemove(itemId);
    } else {
      updates['blockPreferences.dishDislikes'] = arrayUnion(itemId);
      updates['blockPreferences.dishLikes'] = arrayRemove(itemId);
    }
  } else if (kind === 'block') {
    // Vote direct sur bloc (rare : drill-down a été retiré du quiz).
    // On garde la persistance dans blockPreferences.likes / dislikes
    // pour compat admin / vues legacy qui pourraient les lire.
    // Un vote explicite leve une eventuelle neutralisation manuelle.
    updates['blockPreferences.neutral'] = arrayRemove(itemId);
    if (vote === 'like') {
      updates['blockPreferences.likes'] = arrayUnion(itemId);
      updates['blockPreferences.dislikes'] = arrayRemove(itemId);
    } else {
      updates['blockPreferences.dislikes'] = arrayUnion(itemId);
      updates['blockPreferences.likes'] = arrayRemove(itemId);
    }
  }

  return updates;
}

const BLOCK_SENSES = ['like', 'dislike', 'neutral'];

/**
 * Correction manuelle du sens d'un bloc (editeur de gouts).
 * Contrairement a applyVoteToProfile : pas d'increment de votesCount ni de
 * lastQuizAt, une correction n'est pas un vote de quiz. Les trois listes
 * restent exclusives.
 */
export function applyBlockSense(blockId, sense) {
  if (!blockId || !BLOCK_SENSES.includes(sense)) {
    throw new Error(`Invalid applyBlockSense args: blockId=${JSON.stringify(blockId)}, sense=${JSON.stringify(sense)}`);
  }
  return {
    'blockPreferences.likes': sense === 'like' ? arrayUnion(blockId) : arrayRemove(blockId),
    'blockPreferences.dislikes': sense === 'dislike' ? arrayUnion(blockId) : arrayRemove(blockId),
    'blockPreferences.neutral': sense === 'neutral' ? arrayUnion(blockId) : arrayRemove(blockId),
    [PROPAGATION_FREE]: true,
    updatedAt: serverTimestamp()
  };
}

/**
 * Correction d'un vote plat : sense 'like' | 'dislike' | null (annulation).
 * Meme contrat que applyBlockSense : pas un vote, compteur intact.
 */
export function applyDishSense(dishId, sense) {
  if (!dishId || (sense !== 'like' && sense !== 'dislike' && sense !== null)) {
    throw new Error(`Invalid applyDishSense args: dishId=${JSON.stringify(dishId)}, sense=${JSON.stringify(sense)}`);
  }
  const updates = {
    'blockPreferences.dishLikes': sense === 'like' ? arrayUnion(dishId) : arrayRemove(dishId),
    'blockPreferences.dishDislikes': sense === 'dislike' ? arrayUnion(dishId) : arrayRemove(dishId),
    [PROPAGATION_FREE]: true,
    updatedAt: serverTimestamp()
  };
  // Annulation ('jamais servi' retiré) ou « j'aime » : on lève aussi le refus
  // certain issu d'un avis 1-2 étoiles, sinon le plat resterait bloqué malgré la
  // correction.
  if (sense === null || sense === 'like') {
    updates['blockPreferences.lowRatedDishes'] = arrayRemove(dishId);
  }
  return updates;
}

